import Template from './Template31.hbs';
import WordSelection from 'views/components/taskGroups/tasks/wordSelection/WordSelection';
import FragmentTemplate from 'views/components/taskGroups/tasks/wordSelection/Fragment.hbs';
import GapTemplate from 'views/components/taskGroups/tasks/wordSelection/Gap.hbs';
import WordSelectionStyles from 'views/components/taskGroups/tasks/wordSelection/WordSelection.scss';
import QuickInput from 'views/components/quickInput/QuickInput';
import Util from 'util/util';
import TextSimilarityUtil from 'util/TextSimilarityUtil';
import TaskModel from 'models/TaskModel';

export default class Template31 extends WordSelection {

    initialize({
        task_view,
        isPresentation,
    }) {

        _.bindAll(
            this,
            'onEditGap',
            'sendPresentationAnswer',
            'renderFragments',
            'lockAnswer'
        );

        this.taskView = task_view
        this.isPresentation = isPresentation

        this.prepareData();

        // Normalize correct answers.
        this.fragments = _.map(this.fragments, (fragment) => {
            if (fragment.answers) {
                _.each(fragment.answers, (answer) => {
                    answer.text = Util.normalizeCharacters(answer.text);
                });
            }
            return fragment;
        });

        // Set grading strictness
        // Keep compatibility with the old 'caseSensitive' grading
        const { gradingStrictness, caseSensitive } = this.model.get('task_info_json').options ?? {}
        if (gradingStrictness !== undefined) {
            this.gradingStrictness = gradingStrictness
        } else if (caseSensitive) {
            this.gradingStrictness = TaskModel.CASE_SENSITIVE_GRADING
        } else {
            this.gradingStrictness = TaskModel.CASE_INSENSITIVE_GRADING
        }

        this.hasBigGaps = false;
        if (this.model.get('task_info_json').options?.hasBigGaps) {
            this.hasBigGaps = true;
        }

        this.renderTask()
    }

    /**
     * This method will render the template of the task. It will be
     * overwritten with an empty method in /views/components/taskGroups/tasks/Task.js
     * if only the answer view is necessary
     */
    renderTask() {

        this.setElement(Template({
            Styles: WordSelectionStyles
        }));

        // If presentation mode, wait for the parent presentation views to be
        // fully rendered. This is needed to prevent a bug in presentation
        // mode where the fragments are strangely placed. Otherwise render
        // the gap text fragments right away to prevent situations where
        // the Template31 DOM isn't ready when methods as showAnswer or
        // lockAnswer are called.
        if (this.isPresentation) {
            _.defer(this.renderFragments);
        } else {
            this.renderFragments();
        }

        // Debounce the saveAnswer method by 1000 ms so it only fires when more
        // than 1000 ms has elapsed since the last time it was called. This
        // is to prevent DoSS-ing ourselfs every time the user types something
        // in an input field.
        this.saveAnswerDebounced = _.debounce(this.saveAnswer, 2000);

    }

    /**
     * This function appends fragments to the task inside the DOM.
     * Create fragment element for each fragment. Add a highlight
     * if it already has been selected.
     */
    renderFragments() {

        // Reduce gap templates and plain text into a single string and append
        // it to the DOM. This makes it possible for the browser to correct
        // with any invalid HTML automatically without having to normalize
        // and parse each individual fragment, which can result in errors.
        this.$el.append(_.reduce(this.fragments, (content, fragment) => {
            if (fragment instanceof Object) {
                return content + GapTemplate({
                    fragmentID: fragment.id,
                    Styles: WordSelectionStyles
                });
            }
            return content + this.insertPlainText(fragment);
        }, ''))

        // Add gap answer input fields.
        _.each(this.fragments, (fragment) => {
            if (fragment instanceof Object) {

                // Get template we just added by searching for its data-gap-id.
                var gapElement = this.$('.js-gap[data-gap-id=' + fragment.id + ']');

                if (APPLICATION === 'author') {

                    gapElement.append(_.reduce(fragment.answers, (content, gapAnswer) => {
                        return content + FragmentTemplate({
                            Styles: WordSelectionStyles,
                            fragment: gapAnswer.text || '…',
                            fragmentStatus: WordSelectionStyles['fragment--selected']
                        });
                    }, ''));

                } else {

                    var existingAnswer = _.findWhere(this.JSONAnswer, {
                        id: fragment.id
                    }) || '';
                    if (existingAnswer) {
                        existingAnswer = existingAnswer.answer.text;
                    }

                    var answerInput = this.addChildView(new QuickInput({
                        defaultValue: existingAnswer,
                        displayInline: true,
                        noAutoCapitalize: true,
                        noAutoCorrect: true,
                        isWider: this.hasBigGaps
                    }), gapElement);
                    answerInput.$el.on('input', _.partial(this.onEditGap, fragment.id, answerInput, _));

                }
            }
        })

    }

    /**
     * Locks the answer option.
     * User can't give answers.
     */
    lockAnswer() {

        _.invoke(this.getChildViewsOfType(QuickInput), 'disable');

        // When user is following a teacher inside a presentation
        if (this.taskView.sendButton) {
            // Get all QuickInput answer options
            var inputFields = this.getChildViewsOfType(QuickInput);

            // Loop through answer options
            _.each(inputFields, (inputField, index) => {

                // Link the right answer to input field iteratee
                var givenAnswer = _.findWhere(this.savedAnswers, {
                    id: index + 1
                });

                // If there is an answer saved
                if (givenAnswer) {

                    // Set the value of input to the answer text
                    // To make sure any unsaved answers are not checked
                    inputField.setInput(givenAnswer.answer.text);
                } else {

                    // Even if no answer was saved, there could be an unsaved answer
                    // We need to delete this to make sure it isn't checked either
                    inputField.setInput('');
                }
            })

        }

    }

    /**
     * Unlocks the answer option.
     * User can give answers again.
     */
    unlockAnswer() {
        // Call disable function on all QuickInput child views
        _.invoke(this.getChildViewsOfType(QuickInput), 'enable');
    }

    /**
     * Whenever user types something in answerInput input element, save this
     * input as the given answer for the current gap.
     *
     * @param  {number} gapID       ID number of the gap being edited
     * @param  {string} answerInput Current contents of answerInput
     * @param  {Event} e            input change event
     */
    onEditGap(gapID, answerInput, e) {

        this.stopAllEvents(e);

        if (!this.$el.hasClass('showAnswer')) {

            var input = Util.normalizeCharacters(answerInput.getInput());

            this.JSONAnswer = _.clone(this.JSONAnswer);

            var answer = {
                id: gapID,
                answer: {
                    text: input
                }
            };

            var existingAnswerIndex = _.findIndex(this.JSONAnswer, {
                id: gapID
            });
            if (existingAnswerIndex >= 0) {
                if (input) {
                    this.JSONAnswer[existingAnswerIndex] = answer;
                } else {
                    this.JSONAnswer.splice(existingAnswerIndex, 1);
                }
            } else if (input) {
                this.JSONAnswer.push(answer);
            }

            // When the user is not in a presentation we save the answer.
            if (!this.isPresentation) {

                /**
                     * enables us to stop debounced function. flag is set to
                     * false in base class (WordSelection.saveAnswer)
                     */
                this.debounceIsActive = true
                // Save answer
                this.saveAnswerDebounced();
            }

        }

    }

    /**
     * Show the correct answer to the task.
     */
    showAnswer() {

        if (APPLICATION === 'author') {
            return;
        }

        // If a student clicks the show answer button while the debounce is active,
        // save the response first and then show the answer
        if (this.debounceIsActive) {
            this.saveAnswerDebounced.cancel()
            this.saveAnswer()
        }

        _.each(_.filter(this.fragments, _.isObject), (fragment) => {

            let fragmentAnswers = fragment.answers;
            if (this.gradingStrictness !== TaskModel.CASE_SENSITIVE_GRADING) {
                // Create a deep clone where all answers have been transformed to lower case.
                fragmentAnswers = this.transformLowerCase(fragmentAnswers);
            }

            // Get student answer given for the current gap.
            let givenGapAnswer = _.findWhere(this.JSONAnswer, {
                id: fragment.id
            });

            // If there is anything in the current gap, check if this this matches any the model answers for
            // this gap. Else if there is nothing in the current gap, check if this correct and insert an … as
            // a placeholder for this empty answer.
            let isCorrectAnswer;
            if (givenGapAnswer) {
                givenGapAnswer = givenGapAnswer.answer.text;
                var lookupAnswer = Util.normalizeCharacters(givenGapAnswer);
                if (this.gradingStrictness !== TaskModel.CASE_SENSITIVE_GRADING) {
                    lookupAnswer = lookupAnswer.toLowerCase();
                }
                if (this.gradingStrictness === TaskModel.TEXT_SIMILARITY_GRADING) {
                    isCorrectAnswer = this.isSimilarCorrect(lookupAnswer, fragmentAnswers);
                } else {
                    isCorrectAnswer = _.any(fragmentAnswers, { text: lookupAnswer });
                }
            } else {
                isCorrectAnswer = fragmentAnswers[0].text.length === 0;
                givenGapAnswer = '…';
            }

            const gapElement = this.$('.js-gap[data-gap-id=' + fragment.id + ']');
            gapElement.find('> div:has(input)').hide();

            let fragmentStatusClass = (isCorrectAnswer ?
                WordSelectionStyles['fragment--correct'] : WordSelectionStyles['fragment--incorrect']
            ) + ' ' + WordSelectionStyles['fragment--show-answer'];

            if (this.model.get('grading_mode') === 'student') {

                // Check if grading mode is student, because when it is
                // student we want to override the class because we don't want
                // to give any wrong or right indications
                fragmentStatusClass = WordSelectionStyles['fragment--show-answer'];

                // Set correct answer to false to force template to show a tooltip with the
                // correct answer which will tell the user what his/her answer should look
                // like.
                isCorrectAnswer = false;
            }

            gapElement.append(FragmentTemplate({
                Styles: WordSelectionStyles,
                fragment: givenGapAnswer,
                fragmentStatus: fragmentStatusClass,
                showAnswerTooltip: !isCorrectAnswer,
                tooltipText: fragment.answers[0].text || '…'
            }));

            // Make the gap at least as wide as the tooltip, to prevent
            // the tooltip from falling outside of the viewport
            const tooltipWidth = gapElement.find('.js-answer-tooltip').width()
            // Only apply when tool tip is wider than 100px (the min width of a fragment).
            if (tooltipWidth > 100) {
                gapElement.find('.js-fragment').css('min-width', tooltipWidth)
            }

        })

    }

    // Check if lookupAnswer matches any of the fragment answers using either exact
    // or fuzzy matching. Input should already be normalised
    isSimilarCorrect(lookupAnswer, fragmentAnswers) {
        return _.any(fragmentAnswers, (answer) => {
            return TextSimilarityUtil.getTextSimilarity(lookupAnswer, answer.text)
                >= TextSimilarityUtil.SIMILARITY_THRESHOLD;
        });
    }

    /**
     * Transform all answers toLowerCase to make comparing answers easier if case sensitivity does not matter.
     *
     * @param  {Array} answers  answers with unknown capitalisation
     * @return {Array}          answers where all text is in lower case
     */
    transformLowerCase(answers) {
        // Deep-clone answers such that is does not change the this.fragments object.
        return _.map(JSON.parse(JSON.stringify(answers)), (answer) => {
            answer.text = answer.text.toLowerCase();
            return answer;
        })
    }

    getStudentAnswer(responseModel) {

        let answerString = '';
        let JSONAnswer = responseModel.get('json_answer');

        if (JSONAnswer) {

            JSONAnswer = this.castAnswerIDToInt(JSONAnswer);

            _.each(_.filter(this.model.get('task_info_json').text, _.isObject), (gap) => {

                let gapAnswers = gap.answers;

                if (this.gradingStrictness !== TaskModel.CASE_SENSITIVE_GRADING) {
                    gapAnswers = this.transformLowerCase(gapAnswers);
                }

                let fragmentStatus = (
                    WordSelectionStyles['fragment--answer-summary'] + ' ' +
                        WordSelectionStyles['fragment--show-answer'] + ' '
                );
                let fragmentText;

                const givenGapAnswer = _.findWhere(JSONAnswer, { id: gap.id });

                if (givenGapAnswer) {

                    fragmentText = givenGapAnswer.answer.text;

                    if (this.model.get('grading_mode') === 'auto') {

                        let lookupAnswer = Util.normalizeCharacters(givenGapAnswer.answer.text);
                        if (this.gradingStrictness !== TaskModel.CASE_SENSITIVE_GRADING) {
                            lookupAnswer = lookupAnswer.toLowerCase();
                        }

                        // Special case: for an empty model answer, any given answer is correct
                        let givenGapAnswerIsCorrect;
                        if (lookupAnswer && gapAnswers[0].text.length === 0) {
                            givenGapAnswerIsCorrect = true
                        } else if (this.gradingStrictness === TaskModel.TEXT_SIMILARITY_GRADING) {
                            givenGapAnswerIsCorrect = this.isSimilarCorrect(lookupAnswer, gapAnswers);
                        } else {
                            givenGapAnswerIsCorrect = _.findWhere(gapAnswers, { text: lookupAnswer }) !== undefined;
                        }

                        if (givenGapAnswerIsCorrect) {
                            fragmentStatus += WordSelectionStyles['fragment--correct'];
                        } else {
                            fragmentStatus += WordSelectionStyles['fragment--incorrect'];
                        }
                    }
                } else {
                    if (this.model.get('grading_mode') === 'auto') {
                        fragmentStatus += WordSelectionStyles['fragment--incorrect'];
                    }
                    fragmentText = '…';
                }

                answerString += FragmentTemplate({
                    Styles: WordSelectionStyles,
                    fragment: Util.renderContentSafely(fragmentText),
                    fragmentStatus
                });

            })
        }

        return answerString;
    }

    showAuthorAnswer() {
        this.showAnswer()
    }

    /**
     * Hide the correct answer to the task.
     */
    hideAnswer() {
        this.$('.js-gap .js-fragment').remove();
        this.$('.js-gap > div:has(input)').css({
            display: ''
        });
    }

    /**
     * This function is called when user clicks
     * a send button inside a presentation.
     * It will then save the answer in the backend and notify the teacher an answer was given.
     */
    sendPresentationAnswer() {
        this.savedAnswers = this.JSONAnswer;

        this.saveAnswer();
        this.lockAnswer();
    }

    /**
     * If there's a debounced save going on: cancel and
     * directly save.
     */
    beforeDestroy() {

        if (!this.debounceIsActive) {
            return
        }

        this.saveAnswerDebounced.cancel()

        this.saveAnswer()

    }

}
